import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  Target,
  Lightbulb,
  TrendingUp,
  ArrowRight,
  Zap,
  Info
} from "lucide-react";
import { MetodologiaInfoModal } from "./MetodologiaInfoModal";
import { useState } from "react";

interface FluxoTarefasModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const etapas = [
  {
    id: 'lead-capturado',
    nome: 'Lead Capturado',
    descricao: 'Resposta rápida e agendamento de qualificação',
    tempoEstimado: 'menos de 5 minutos',
    objetivo: 'Fazer primeiro contato imediato e criar rapport inicial',
    pontosAtencao: [
      '⚡ Leads contatados em menos de 5 minutos são 100x mais propensos a converter!',
      'Agendar reunião de qualificação (15-30 minutos)',
      'Coletar informações básicas: nome, telefone, e-mail, endereço, valor da conta',
      'Confirmar interesse e criar conexão inicial'
    ],
    tarefasPrincipais: [
      'Primeiro Contato Imediato (menos de 5 minutos)',
      'Coleta de Dados Iniciais',
      'Agendar Reunião de Qualificação',
      'Registrar Informações no CRM'
    ],
    criteriosProgressao: [
      'Primeiro contato realizado com sucesso',
      'Dados básicos coletados',
      'Reunião de qualificação agendada'
    ]
  },
  {
    id: 'qualificacao',
    nome: 'Qualificação',
    descricao: 'Aplicar BANT e classificar temperatura do lead',
    tempoEstimado: '15-45 minutos',
    objetivo: 'Qualificar o lead completamente usando metodologia BANT e SPIN',
    pontosAtencao: [
      'Aplicar framework BANT completo (Budget, Authority, Need, Timeline)',
      'Fazer perguntas SPIN: Situação, Problema, Implicação, Necessidade',
      'Solicitar documentos: conta de energia e fotos do telhado',
      'Classificar temperatura: QUENTE, MORNO ou FRIO',
      'Análise técnica inicial baseada nos documentos'
    ],
    tarefasPrincipais: [
      'Aplicar Framework BANT',
      'SPIN - Perguntas de Situação',
      'SPIN - Perguntas de Problema',
      'Solicitar Documentos',
      'Classificar Temperatura do Lead'
    ],
    criteriosProgressao: [
      'BANT completamente preenchido',
      'Lead tem orçamento ou acesso a financiamento',
      'É tomador de decisão ou acesso garantido',
      'Necessidade clara identificada',
      'Timeline definido',
      'Conta de energia recebida (> R$ 200)',
      'Próxima etapa agendada'
    ]
  },
  {
    id: 'analise-necessidades',
    nome: 'Análise de Necessidades',
    descricao: 'Análise técnica e exploração de implicações (SPIN)',
    tempoEstimado: '7-14 dias',
    objetivo: 'Realizar análise técnica completa e educar o cliente',
    pontosAtencao: [
      'Análise técnica preliminar com base nos documentos',
      'SPIN - Perguntas de Implicação: explorar consequências de não resolver',
      'SPIN - Perguntas de Necessidade: visualizar benefícios da solução',
      'Educação do cliente sobre energia solar',
      'Dimensionamento preliminar do sistema',
      'Definir necessidade de visita técnica'
    ],
    tarefasPrincipais: [
      'Análise Técnica Inicial',
      'SPIN - Perguntas de Implicação',
      'SPIN - Perguntas de Necessidade de Solução',
      'Educação do Cliente',
      'Dimensionamento Preliminar',
      'Definir Necessidade de Visita Técnica'
    ],
    criteriosProgressao: [
      'Análise técnica concluída',
      'Implicações e necessidades exploradas',
      'Dimensionamento preliminar realizado',
      'Cliente educado sobre energia solar',
      'Decisão sobre visita técnica tomada'
    ]
  },
  {
    id: 'visita-tecnica',
    nome: 'Visita Técnica',
    descricao: 'Validar análise técnica in loco e fortalecer relacionamento',
    tempoEstimado: '1-2 horas',
    objetivo: 'Confirmar viabilidade técnica e construir confiança',
    pontosAtencao: [
      'Preparar equipamentos: trena, bússola, câmera, tablet',
      'Inspeção completa do telhado: dimensões, tipo, sombreamento, orientação',
      'Inspeção da infraestrutura elétrica: padrão, disjuntor, quadro',
      'Construir relacionamento: demonstrar profissionalismo e conhecimento',
      'Documentar tudo: fotos (mínimo 10), medições, observações',
      'Follow-up imediato no mesmo dia'
    ],
    tarefasPrincipais: [
      'Preparação Pré-Visita',
      'Inspeção do Telhado',
      'Inspeção da Infraestrutura Elétrica',
      'Construção de Relacionamento',
      'Documentação Pós-Visita',
      'Follow-up Pós-Visita'
    ],
    criteriosProgressao: [
      'Visita técnica concluída com sucesso',
      'Viabilidade técnica confirmada',
      'Todas as medições e fotos coletadas',
      'Não há impedimentos técnicos relevantes',
      'Cliente mantém interesse'
    ]
  },
  {
    id: 'proposta-valor',
    nome: 'Proposta de Valor',
    descricao: 'Apresentar solução personalizada',
    tempoEstimado: '3-5 dias',
    objetivo: 'Apresentar proposta completa e tratar objeções',
    pontosAtencao: [
      'Elaborar proposta completa: dimensionamento, layout visual, análise financeira',
      'Criar layout visual dos painéis no telhado',
      'Calcular economia mensal, payback e ROI',
      'Agendar apresentação presencial ou remota',
      'Preparar resposta para objeções comuns',
      'Apresentar proposta de forma clara e objetiva',
      'Follow-up pós-apresentação no mesmo dia'
    ],
    tarefasPrincipais: [
      'Preparar Proposta de Valor',
      'Agendar Apresentação da Proposta',
      'Apresentar Proposta de Valor',
      'Follow-up Pós-Apresentação'
    ],
    criteriosProgressao: [
      'Proposta apresentada com sucesso',
      'Cliente compreendeu todos os aspectos',
      'Proposta está alinhada com expectativas',
      'Não há objeções intransponíveis',
      'Prazo para retorno definido'
    ]
  },
  {
    id: 'follow-up',
    nome: 'Follow-up e Negociação',
    descricao: 'Manter engajamento e conduzir ao fechamento',
    tempoEstimado: '7-21 dias',
    objetivo: 'Aplicar cadência estruturada e tratar objeções',
    pontosAtencao: [
      'Aplicar cadência de 5-8 touchpoints multi-canal',
      'Sempre agregar valor: vídeos, cases, infográficos',
      'Identificar e tratar objeções: preço, comparação, timing, medo técnico',
      'Identificar sinais de compra: perguntas sobre instalação, prazos, pagamento',
      'Saber quando parar: break-up message após 7-8 tentativas sem engajamento',
      'Ajustar proposta se necessário'
    ],
    tarefasPrincipais: [
      'Aplicar cadência de follow-up estruturada',
      'Identificar e tratar objeções',
      'Agregar valor contínuo',
      'Identificar sinais de compra',
      'Ajustar proposta se necessário'
    ],
    criteriosProgressao: [
      'Cliente demonstra sinais claros de compra',
      'Todas as objeções foram tratadas',
      'Cliente verbalizou intenção de prosseguir',
      'Decisores alinhados',
      'Forma de pagamento definida'
    ]
  },
  {
    id: 'fechamento',
    nome: 'Fechamento',
    descricao: 'Formalizar o acordo',
    tempoEstimado: '1-2 dias',
    objetivo: 'Assinar contrato e iniciar processo de instalação',
    pontosAtencao: [
      'Confirmar todos os detalhes da proposta',
      'Preparar documentação: contrato, ordem de serviço, termos',
      'Reunião de fechamento: explicar contrato completamente',
      'Coletar assinaturas e pagamento inicial',
      'Handoff para equipe técnica com todas as informações',
      'Comunicação pós-fechamento: agradecimento e cronograma'
    ],
    tarefasPrincipais: [
      'Confirmar todos os detalhes',
      'Preparar documentação para fechamento',
      'Reunião de fechamento',
      'Assinatura do contrato',
      'Processamento do pagamento',
      'Handoff para equipe técnica'
    ],
    criteriosProgressao: [
      'Contrato assinado por todas as partes',
      'Pagamento inicial recebido',
      'Documentação completa arquivada',
      'Equipe técnica informada',
      'Cronograma definido'
    ]
  }
];

export function FluxoTarefasModal({ open, onOpenChange }: FluxoTarefasModalProps) {
  const [showMetodologiaInfo, setShowMetodologiaInfo] = useState(false);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            Fluxo de Tarefas por Lead - Metodologia de Vendas
          </DialogTitle>
          <DialogDescription>
            Guia completo do processo de vendas com pontos de atenção para otimizar performance
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[calc(90vh-120px)] pr-4">
          <div className="space-y-6">
            {/* Introdução */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2 mb-2">
                      <Lightbulb className="h-5 w-5 text-blue-600" />
                      Metodologia SPIN Selling + BANT
                    </CardTitle>
                    <CardDescription>
                      Este fluxo guia você através de cada etapa do funil de vendas, com tarefas específicas e pontos de atenção para maximizar conversões.
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMetodologiaInfo(true)}
                    className="flex items-center gap-2"
                  >
                    <Info className="h-4 w-4" />
                    Entender Metodologias
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Etapas */}
            {etapas.map((etapa, index) => (
              <Card key={etapa.id} className="relative pl-10">
                <div className="absolute left-2 top-6 flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full font-bold text-sm z-10">
                  {index + 1}
                </div>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{etapa.nome}</CardTitle>
                      <CardDescription className="text-base mb-3">{etapa.descricao}</CardDescription>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {etapa.tempoEstimado}
                        </Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Target className="h-3 w-3" />
                          {etapa.objetivo}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Pontos de Atenção */}
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-orange-600">
                      <AlertTriangle className="h-4 w-4" />
                      Pontos de Atenção para Performance
                    </h4>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      {etapa.pontosAtencao.map((ponto, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Zap className="h-3 w-3 mt-0.5 text-yellow-500 flex-shrink-0" />
                          <span>{ponto}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Tarefas Principais */}
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-blue-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Tarefas Principais
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {etapa.tarefasPrincipais.map((tarefa, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-blue-50 rounded">
                          <CheckCircle2 className="h-3 w-3 text-blue-600 flex-shrink-0" />
                          <span>{tarefa}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Critérios de Progressão */}
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-600">
                      <TrendingUp className="h-4 w-4" />
                      Critérios para Avançar para Próxima Etapa
                    </h4>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      {etapa.criteriosProgressao.map((criterio, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-3 w-3 mt-0.5 text-green-600 flex-shrink-0" />
                          <span>{criterio}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Seta para próxima etapa */}
                  {index < etapas.length - 1 && (
                    <div className="flex justify-center pt-2">
                      <ArrowRight className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Dicas Finais */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-green-600" />
                  Dicas para Sucesso
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Zap className="h-4 w-4 mt-0.5 text-yellow-500" />
                    <span><strong>Velocidade é crucial:</strong> Leads contatados em {'< '}5 minutos convertem 100x mais!</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="h-4 w-4 mt-0.5 text-yellow-500" />
                    <span><strong>Sempre agregue valor:</strong> Não seja invasivo, ofereça conteúdo útil em cada contato.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="h-4 w-4 mt-0.5 text-yellow-500" />
                    <span><strong>Ouça mais do que fala:</strong> Use perguntas SPIN para descobrir necessidades reais.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="h-4 w-4 mt-0.5 text-yellow-500" />
                    <span><strong>Documente tudo:</strong> Anote cada interação no CRM para referência futura.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="h-4 w-4 mt-0.5 text-yellow-500" />
                    <span><strong>Seja consultivo:</strong> Ajude o cliente a tomar a melhor decisão, não apenas venda.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
    
    <MetodologiaInfoModal open={showMetodologiaInfo} onOpenChange={setShowMetodologiaInfo} />
    </>
  );
}

